// v49.3 — Reusable button + input primitives.
// Centralised so every form across the app shares the same visual tokens.

import { forwardRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success";

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

export function Button({ variant = "primary", className = "", disabled, children, ...rest }: BtnProps) {
  const base = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lc-md text-sm font-extrabold transition-colors cursor-pointer disabled:cursor-not-allowed";
  const tones: Record<Variant, string> = {
    primary:   "bg-gradient-to-br from-lc-blue to-lc-blue-dark text-white hover:shadow-lg disabled:from-lc-line disabled:to-lc-line disabled:text-lc-faint disabled:shadow-none",
    secondary: "bg-white text-lc-ink-2 border border-lc-line hover:bg-lc-bg-2 disabled:opacity-50",
    ghost:     "bg-transparent text-lc-mute hover:bg-lc-bg-2 hover:text-lc-ink disabled:opacity-50",
    danger:    "bg-lc-red-bg text-lc-red-fg border border-red-200 hover:bg-red-100 disabled:opacity-50",
    success:   "bg-gradient-to-br from-lc-green to-emerald-700 text-white hover:shadow-lg disabled:from-lc-line disabled:to-lc-line disabled:text-lc-faint",
  };
  return (
    <button {...rest} disabled={disabled} className={`${base} ${tones[variant]} ${className}`}>
      {children}
    </button>
  );
}

export function Label({ children }: { children: React.ReactNode }) {
  return <span className="text-[10px] font-extrabold tracking-widest uppercase text-lc-mute">{children}</span>;
}

export function TextInput({ label, ...rest }: { label?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <input
        {...rest}
        className={`px-3 py-2.5 rounded-lc-md border border-lc-line bg-white text-sm text-lc-ink placeholder:text-lc-faint outline-none focus:border-lc-blue focus:ring-2 focus:ring-lc-blue-light ${rest.className || ""}`}
      />
    </label>
  );
}

/* GH #21 — show/hide toggle for password fields. Drop-in for a bare
   `<input type="password" className="..." />`: pass the exact same
   className/props, the eye button is absolutely positioned inside the
   existing field rather than changing its layout. Used anywhere a
   password/passphrase is typed (Sign In, Register, Reset Password,
   Crypto Wallet) so the UX is consistent everywhere, not patched per-page. */
/* forwardRef added so React Hook Form can register this input: spreading
   `{...register("password")}` passes a ref, and without it RHF cannot read the
   value of an uncontrolled field. Purely additive — every existing caller
   passes no ref and is unaffected. */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  { label?: string } & InputHTMLAttributes<HTMLInputElement>
>(function PasswordInput({ label, className = "", ...rest }, ref) {
  const [visible, setVisible] = useState(false);
  const input = (
    <div className="relative">
      <input
        ref={ref}
        {...rest}
        type={visible ? "text" : "password"}
        className={`w-full pr-10 ${className}`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        className="absolute right-0 top-0 bottom-0 px-3 flex items-center text-lc-faint hover:text-lc-mute"
      >
        {/* eye-icon fix — icon must reflect current state (open eye =
            currently visible, slashed eye = currently hidden), not the
            inverse. Was showing the slashed glyph while the password was
            visible and the open glyph while it was masked. */}
        {visible ? <EyeGlyph /> : <EyeOffGlyph />}
      </button>
    </div>
  );
  if (!label) return input;
  return (
    <label className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {input}
    </label>
  );
});

function EyeGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.87 19.87 0 0 1 4.22-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.87 19.87 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function TextArea({ label, ...rest }: { label?: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <label className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <textarea
        {...rest}
        className={`px-3 py-2.5 rounded-lc-md border border-lc-line bg-white text-sm text-lc-ink placeholder:text-lc-faint outline-none focus:border-lc-blue focus:ring-2 focus:ring-lc-blue-light resize-y min-h-[60px] ${rest.className || ""}`}
      />
    </label>
  );
}

export function Select({ label, children, ...rest }: { label?: string; children: React.ReactNode } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <label className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <select
        {...rest}
        className={`px-3 py-2.5 rounded-lc-md border border-lc-line bg-white text-sm text-lc-ink outline-none focus:border-lc-blue focus:ring-2 focus:ring-lc-blue-light cursor-pointer ${rest.className || ""}`}
      >
        {children}
      </select>
    </label>
  );
}

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white border border-lc-line rounded-lc-lg p-5 ${className}`}>{children}</div>
  );
}

export function CardSection({ title, subtitle, children }: { title?: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      {(title || subtitle) && (
        <div>
          {title && <h3 className="text-base font-extrabold text-lc-ink tracking-tight">{title}</h3>}
          {subtitle && <p className="text-xs text-lc-mute mt-1 leading-snug">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

/* v50.178 / v50.179 — WCAG 2.2 AA: status pills convey state by TEXT +
   ICON, not colour alone. Auto-injects a semantic glyph based on tone:
     • green → ✓ (success / OK)
     • amber → ⚠ (warning / pending — needs attention)
     • red   → ✕ (error / blocked / failed / overdue)
   Blue and neutral tones don't auto-inject — they're typically
   categorical (informational chips, kind tags) rather than status.
   Callers needing a different icon pass icon={<...>}; explicit
   icon={null} suppresses the auto-inject for the rare decorative
   green/amber/red pill. */
export function Pill({ tone = "neutral", icon, children }: {
  tone?:    "neutral" | "blue" | "green" | "amber" | "red";
  icon?:    React.ReactNode | null;
  children: React.ReactNode;
}) {
  const tones = {
    neutral: "bg-lc-bg-2 text-lc-mute border-lc-line",
    blue:    "bg-lc-blue-light text-lc-blue-dark border-lc-blue-border",
    green:   "bg-lc-green-bg text-lc-green-fg border-green-200",
    amber:   "bg-lc-amber-bg text-lc-amber-fg border-amber-200",
    red:     "bg-lc-red-bg text-lc-red-fg border-red-200",
  };
  /* undefined → auto-inject per tone; null → caller explicitly opted out;
     anything else → caller supplied a custom glyph. */
  const finalIcon: React.ReactNode | null =
    icon === undefined
      ? (tone === "green" ? <PillGlyphOk />
        : tone === "amber" ? <PillGlyphWarn />
        : tone === "red"   ? <PillGlyphError />
        : null)
      : icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide border ${tones[tone]}`}>
      {finalIcon && <span aria-hidden="true" className="inline-flex shrink-0">{finalIcon}</span>}
      {children}
    </span>
  );
}

/* v50.179 — Semantic glyphs for the auto-injected Pill icons. Inline
   SVGs sized to match the pill's 10px text. currentColor inherits the
   pill's tone fg so colour stays consistent with the existing palette.
   aria-hidden is set on the wrapping span in Pill, so these don't need
   their own. */
function PillGlyphOk() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function PillGlyphWarn() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L1 21h22L12 2zm-1 6h2v8h-2V8zm0 10h2v2h-2v-2z" />
    </svg>
  );
}
function PillGlyphError() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
